import React, { useState, useEffect } from 'react';
import { Clock, User, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/lib/api';

export function ActivityLog({ entityType, entityId }) {
  const [logs, setLogs] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!entityId || !expanded) return;
    const fetchLogs = (showLoading) => {
      if (showLoading) setLoading(true);
      api.get(`/activity-log/${entityType}/${entityId}`)
        .then(res => setLogs(res.data || []))
        .catch(() => setLogs([]))
        .finally(() => showLoading && setLoading(false));
    };
    fetchLogs(true);
    // Keep the log fresh while it stays expanded, so new entries (send,
    // payment recorded, cancel, dispute, etc.) show up without requiring
    // the user to collapse/re-expand the section.
    const interval = setInterval(() => fetchLogs(false), 4000);
    return () => clearInterval(interval);
  }, [entityType, entityId, expanded]);

  if (!entityId) return null;

  return (
    <div className="mt-4 border-t border-gray-100 pt-3" data-testid="activity-log-section">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors w-full"
        data-testid="activity-log-toggle"
      >
        <Clock className="w-3.5 h-3.5" />
        Activity Log
        {expanded ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
        {logs.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-[10px]">{logs.length}</span>}
      </button>
      {expanded && (
        <div className="mt-2 max-h-48 overflow-y-auto space-y-1.5" data-testid="activity-log-entries">
          {loading ? (
            <p className="text-xs text-gray-400 py-2 text-center">Loading...</p>
          ) : logs.length === 0 ? (
            <p className="text-xs text-gray-400 py-2 text-center">No activity recorded yet</p>
          ) : (
            [...logs].reverse().map((entry, idx) => (
              <div key={entry.id || idx} className="flex items-start gap-2 py-1.5 px-2 rounded bg-gray-50/80 text-xs" data-testid={`activity-entry-${idx}`}>
                <User className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-700">{entry.user_name || 'System'}</span>
                  <span className="text-gray-400 mx-1">·</span>
                  <span className="text-gray-600 capitalize">{(entry.action || '').replace(/_/g, ' ')}</span>
                  {entry.details && <span className="text-gray-400 ml-1">— {entry.details}</span>}
                </div>
                <span className="text-[10px] text-gray-400 flex-shrink-0 whitespace-nowrap">
                  {entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
